import { MapPin, Ship, Anchor, Package, Radio, Navigation } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TrackingEvent {
  date: string;
  location: string;
  description: string;
  status?: string;
}

const EVENT_ICONS: Record<string, typeof Ship> = {
  "cargado": Package,
  "tránsito": Ship,
  "puerto": Anchor,
  "descargado": Anchor,
  "entregado": MapPin,
  "nacionalizado": Navigation,
};

function getEventIcon(description: string) {
  const lower = description.toLowerCase();
  for (const [key, Icon] of Object.entries(EVENT_ICONS)) {
    if (lower.includes(key)) return Icon;
  }
  return Radio;
}

export function TrackingTimeline({ events }: { events: TrackingEvent[] }) {
  if (!events.length) return null;

  return (
    <div className="relative pl-6 space-y-0">
      {/* Vertical line */}
      <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-border" />

      {events.map((event, idx) => {
        const Icon = getEventIcon(event.description);
        const isFirst = idx === 0;

        return (
          <div key={idx} className="relative flex items-start gap-3 pb-6 last:pb-0">
            {/* Dot / Icon */}
            <div
              className={cn(
                "absolute -left-6 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-background z-10",
                isFirst
                  ? "border-primary text-primary"
                  : "border-muted-foreground/30 text-muted-foreground"
              )}
            >
              <Icon className="h-3 w-3" />
            </div>

            {/* Content */}
            <div className="ml-2 min-w-0">
              <p className={cn(
                "text-sm font-medium",
                isFirst ? "text-foreground" : "text-muted-foreground"
              )}>
                {event.description}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground">
                  {new Date(event.date).toLocaleDateString("es-CO", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                {event.location && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                      <MapPin className="h-3 w-3" />
                      {event.location}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
